/**
 * Structure views, the pure part (M3 plan R13): the view diff, the structure-state → relay-core mapping, the reboot
 * progress, the per-key free list and the enter hook's preload list. No three.js here, so it all tests in Node.
 */
import type { SystemLayout } from '../../galaxy/layout/types'
import type { FactionId } from '../../render/contracts'
import type { CoreState } from '../../render/reachFx/relayCore'
import type { ActiveStructure, RelinkState } from '../../reach/runtime/types'
import type { StructuresConfig } from './config'

/** What a live view was built from: a view is kept only while all four still match. */
export interface StructureViewKey {
  readonly kind: ActiveStructure['kind']
  readonly id: string
  readonly assetId: string | null
  readonly look: FactionId
}

/** The diff between the live views (`prev`) and the session's structures (`next`). Indices point into the inputs. */
export interface StructureViewPlan {
  /** `next` indices that need a new view. */
  readonly create: number[]
  /** `prev` indices whose views go (released to the pool, or disposed). */
  readonly dispose: number[]
  /** [prev index, next index]: views that stay and rebind to the (possibly new) structure object. */
  readonly keep: [number, number][]
}

function identity(kind: string, id: string): string {
  return `${kind}\u0000${id}`
}

/**
 * Decides which views to create, dispose and keep (a rare event: `structuresVersion` changed). A view is kept when a
 * structure with the same kind and id is still listed with the same asset and look; a changed asset or look replaces
 * it. Duplicate identities in `next` match at most one view; the rest get their own.
 */
export function planStructureViews(prev: readonly StructureViewKey[], next: readonly ActiveStructure[]): StructureViewPlan {
  const byIdentity = new Map<string, number>()
  for (let i = 0; i < prev.length; i++) {
    const key = identity(prev[i].kind, prev[i].id)
    if (!byIdentity.has(key)) byIdentity.set(key, i)
  }
  const used = new Set<number>()
  const create: number[] = []
  const keep: [number, number][] = []
  for (let j = 0; j < next.length; j++) {
    const s = next[j]
    const i = byIdentity.get(identity(s.kind, s.id))
    if (i !== undefined && !used.has(i) && prev[i].assetId === s.assetId && prev[i].look === s.look) {
      used.add(i)
      keep.push([i, j])
    } else {
      create.push(j)
    }
  }
  const dispose: number[] = []
  for (let i = 0; i < prev.length; i++) if (!used.has(i)) dispose.push(i)
  return { create, dispose, keep }
}

/** Whether a structure gets a relay core at its `DOCK_TRANSIT` socket (relays and lane rings). */
export function hasCore(kind: ActiveStructure['kind']): boolean {
  return kind === 'relay' || kind === 'laneRing'
}

/**
 * `ActiveStructure.state` as a core state. Relays: 0 offline, 1 online, 2 reboot. Lane rings: 0 down, 1 up (a lane
 * ring never reboots). Anything else reads as dark (0).
 */
export function coreStateOf(kind: ActiveStructure['kind'], state: number): CoreState {
  if (state === 1) return 1
  if (state === 2 && kind === 'relay') return 2
  return 0
}

/** The reboot progress 0..1 of relay `relayIndex` (galaxy relay index): elapsed / duration while it reboots, else 0. */
export function rebootProgress(relink: RelinkState, relayIndex: number): number {
  if (relink.phase !== 'reboot' || relink.relayIndex !== relayIndex || !(relink.duration > 0)) return 0
  const p = relink.t / relink.duration
  return p > 0 ? (p < 1 ? p : 1) : 0
}

/** The free-list key of a pooled view: one list per (asset id, look) pair, since the look is baked into materials. */
export function poolKey(assetId: string, look: FactionId): string {
  return `${assetId}|${look}`
}

/** The asset id part of a pool key. */
export function poolKeyAsset(key: string): string {
  const bar = key.lastIndexOf('|')
  return bar < 0 ? key : key.slice(0, bar)
}

/** A small free list per key. `release` refuses (returns false) when the key's list is full; the caller disposes. */
export interface ViewPool<T> {
  acquire(key: string): T | undefined
  release(key: string, item: T): boolean
  /** Pooled items under `key`. */
  count(key: string): number
  /** Pooled items in total. */
  size(): number
  /** Removes (and hands to `drop`) every pooled item whose key fails `keepKey`. */
  trim(keepKey: (key: string) => boolean, drop: (item: T) => void): number
  /** Removes every pooled item, handing each to `drop`. */
  clear(drop: (item: T) => void): void
}

export function createViewPool<T>(maxPerKey: number): ViewPool<T> {
  const lists = new Map<string, T[]>()
  return {
    acquire(key: string): T | undefined {
      return lists.get(key)?.pop()
    },
    release(key: string, item: T): boolean {
      let list = lists.get(key)
      if (!list) {
        list = []
        lists.set(key, list)
      }
      if (list.length >= maxPerKey) return false
      list.push(item)
      return true
    },
    count(key: string): number {
      return lists.get(key)?.length ?? 0
    },
    size(): number {
      let n = 0
      for (const list of lists.values()) n += list.length
      return n
    },
    trim(keepKey: (key: string) => boolean, drop: (item: T) => void): number {
      let n = 0
      for (const [key, list] of lists) {
        if (keepKey(key)) continue
        for (const item of list) drop(item)
        n += list.length
        lists.delete(key)
      }
      return n
    },
    clear(drop: (item: T) => void): void {
      for (const list of lists.values()) for (const item of list) drop(item)
      lists.clear()
    },
  }
}

/** One (asset id, look) pair the enter hook preloads, with how many views. */
export interface PreloadItem {
  readonly kind: ActiveStructure['kind']
  readonly assetId: string
  readonly look: FactionId
  readonly count: number
}

/**
 * The views to have ready for `layout`: every distinct (asset id, look) pair among its stations, relays, lane rings
 * and model-backed POIs (derelicts are built, not instantiated), up to `preloadPerKey` each and never more than the
 * layout places. Stations carry their look; the others use `preloadLooks`, unless `sites` (the session's own
 * structure list for `layout`, R12's `structureSites`) is given, whose looks then win. Sorted by asset id, then look.
 */
export function preloadList(
  layout: SystemLayout,
  cfg: StructuresConfig,
  sites?: readonly Pick<ActiveStructure, 'kind' | 'assetId' | 'look'>[],
): PreloadItem[] {
  const counts = new Map<string, { kind: ActiveStructure['kind']; assetId: string; look: FactionId; n: number }>()
  const add = (kind: ActiveStructure['kind'], assetId: string, look: FactionId): void => {
    const key = poolKey(assetId, look)
    const entry = counts.get(key)
    if (entry) entry.n++
    else counts.set(key, { kind, assetId, look, n: 1 })
  }
  if (sites !== undefined) {
    for (const site of sites) if (site.assetId !== null) add(site.kind, site.assetId, site.look)
  } else {
    for (const s of layout.stations) add('station', s.assetId, s.look)
    for (const r of layout.relays) add('relay', r.assetId, cfg.preloadLooks.relay)
    for (const r of layout.laneRings) add('laneRing', r.assetId, cfg.preloadLooks.laneRing)
    for (const p of layout.pois) if (p.assetId !== null) add('poi', p.assetId, cfg.preloadLooks.poi)
  }
  const out: PreloadItem[] = []
  for (const e of counts.values()) {
    const count = Math.min(e.n, cfg.preloadPerKey)
    if (count > 0) out.push({ kind: e.kind, assetId: e.assetId, look: e.look, count })
  }
  out.sort((a, b) => (a.assetId === b.assetId ? (a.look < b.look ? -1 : a.look > b.look ? 1 : 0) : a.assetId < b.assetId ? -1 : 1))
  return out
}

/** Every structure asset id `layout` uses (the pool keeps these across a trim). */
export function layoutAssetIds(layout: SystemLayout): Set<string> {
  const ids = new Set<string>()
  for (const s of layout.stations) ids.add(s.assetId)
  for (const r of layout.relays) ids.add(r.assetId)
  for (const r of layout.laneRings) ids.add(r.assetId)
  for (const p of layout.pois) if (p.assetId !== null) ids.add(p.assetId)
  return ids
}
